import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop Component
 * 
 * Handles automatic scroll behavior on route changes:
 * - Scrolls to top (0, 0) when navigating to a new page
 * - Scrolls to specific section when URL contains a hash (#section-id)
 * - Accounts for fixed navigation header offset
 * - Implements retry mechanism for sections that may load asynchronously
 * 
 * Usage: Place this component inside Router context in App.tsx
 */
export const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL (e.g., /products/iot#aiCooler)
    if (location.hash) {
      const sectionId = location.hash.slice(1); // Remove the '#' character
      let retryCount = 0;
      const maxRetries = 10; // Try up to 10 times
      const retryInterval = 100; // Wait 100ms between retries

      // Function to attempt scrolling to the section
      const attemptScroll = () => {
        const element = document.getElementById(sectionId);
        
        if (element) {
          // Element found! Calculate offset for fixed header
          const headerOffset = 100; // Navigation height + padding
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;

          // Smooth scroll to section
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          
          // Add focus to the section for accessibility
          element.setAttribute('tabindex', '-1');
          element.focus({ preventScroll: true });
          
          return true; // Success
        }
        
        return false; // Element not found yet
      };

      // Retry logic with increasing delays
      const scrollWithRetry = () => {
        // Try immediately first
        if (attemptScroll()) {
          return;
        }

        // If not found, retry with intervals
        const intervalId = setInterval(() => {
          retryCount++;
          
          if (attemptScroll()) {
            // Success - stop retrying
            clearInterval(intervalId);
          } else if (retryCount >= maxRetries) {
            // Max retries reached - scroll to top as fallback
            clearInterval(intervalId);
            console.warn(`Section with id "${sectionId}" not found after ${maxRetries} attempts. Scrolling to top.`);
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        }, retryInterval);

        // Cleanup function
        return () => clearInterval(intervalId);
      };

      // Start the retry process after a short delay to allow page transition
      const timeoutId = setTimeout(() => {
        const cleanup = scrollWithRetry();
        return cleanup;
      }, 200);

      return () => clearTimeout(timeoutId);
    } else {
      // No hash - scroll to top immediately when navigating to a new page
      // Use 'instant' to prevent smooth scroll from previous page position
      window.scrollTo({
        top: 0,
        behavior: 'instant' as ScrollBehavior
      });
    }
  }, [location.pathname, location.hash]); // Re-run when path or hash changes

  return null; // This component doesn't render anything
};
